/** The panel's HTTP client. The session travels only in an HttpOnly cookie. */

import { translateMetaError } from "../lib/meta-errors";

const GENERIC_ERROR = "No se pudo completar la acción";

/** An answer the server refused, with its status so the caller can tell a 404 from a 500. */
export class ApiError extends Error {
  constructor(
    message: string,
    readonly status: number,
  ) {
    super(message);
    this.name = "ApiError";
  }
}

/**
 * Where an expired session goes: to sign in, and back to the same page afterwards.
 *
 * It is a full navigation and not a route change on purpose: the session is the server's,
 * and whatever the panel had in memory belongs to the session that just ended.
 */
export function redirectToLogin(): void {
  if (typeof window === "undefined") return;
  const { pathname, search } = window.location;
  if (pathname === "/login") return;
  window.location.assign(`/login?next=${encodeURIComponent(pathname + search)}`);
}

export interface ApiClientOptions {
  onUnauthorized?: () => void;
}

export function createApiClient({ onUnauthorized = redirectToLogin }: ApiClientOptions = {}) {
  async function request<T>(url: string, options: RequestInit = {}): Promise<T> {
    // A file goes up as `FormData`, and the browser is what writes its `content-type`: it
    // carries the multipart boundary, which nothing here could know.
    const isForm = typeof FormData !== "undefined" && options.body instanceof FormData;
    const response = await fetch(url, {
      ...options,
      cache: "no-store",
      credentials: "same-origin",
      headers: {
        ...(isForm ? {} : { "content-type": "application/json" }),
        ...(options.headers as Record<string, string> | undefined),
      },
    });

    const data = (await response.json().catch(() => ({}))) as T & { error?: string };
    if (response.status === 401) onUnauthorized();
    // The server's own errors already arrive in Spanish; the ones bouncing back from Meta
    // do not. They are translated here, at the single point they all pass through, rather
    // than in every notice.
    if (!response.ok) throw new ApiError(translateMetaError(data.error) || GENERIC_ERROR, response.status);
    return data;
  }

  return {
    get: <T>(url: string) => request<T>(url),
    post: <T>(url: string, body?: unknown) => request<T>(url, { method: "POST", body: JSON.stringify(body ?? {}) }),
    put: <T>(url: string, body?: unknown) => request<T>(url, { method: "PUT", body: JSON.stringify(body ?? {}) }),
    patch: <T>(url: string, body?: unknown) => request<T>(url, { method: "PATCH", body: JSON.stringify(body ?? {}) }),
    delete: <T>(url: string) => request<T>(url, { method: "DELETE" }),
    upload: <T>(url: string, file: Blob) => {
      const form = new FormData();
      form.append("file", file);
      return request<T>(url, { method: "POST", body: form });
    },
  };
}

export type ApiClient = ReturnType<typeof createApiClient>;

/** The one client the whole panel shares. */
export const api = createApiClient();
